import { useCallback, useRef } from 'react';

import { makeAuthenticatedApiCall } from '../../../../apiHelper';
import { formatOcrFields, traceIa } from '../../iaTrace';
import { CgDocType, CgOcrResponse, isCgDocType } from '../../ocr/ocrResponseTypes';
import {
  type ExtractedFieldEntry,
  type IdentityPoolEntry,
  type IngestionItem,
  type UploadedDocResult,
  integrateDocResult,
  retagIngestionItems,
  useFormCGIAStore,
} from '../../store/useFormCGIAStore';
import { KbisPoolEntry } from '../kbisRouting';
import { PdfSplitPart, buildSplitPartItems } from '../pdfSplitParts';
import { UploadRejectedError, uploadFileToFirebase } from '../../../../utils/firebaseUpload';

const MAX_CONCURRENT_UPLOADS = 3;
// 5 OCR en parallèle : Skipcar n'est pas sur le free-tier Anthropic
// (limites prod permettent largement 5 simultanées par agence). Le retry
// exponentiel 1s/3s/7s en aval encaisse les throttles ponctuels résiduels.
const MAX_CONCURRENT_OCR = 5;

/**
 * Backoff exponentiel 1s / 3s / 7s sur erreur transitoire. Au delà : échec final.
 */
const RETRY_DELAYS_MS = [1000, 3000, 7000];

const sleep = (ms: number) => new Promise((resolve) => setTimeout(resolve, ms));

/**
 * Types encodant un RÔLE (et non une nature de pièce) → type de base que le
 * modèle d'extraction renverra réellement. Miroir de `ROLE_SCOPED_DOC_TYPES`
 * côté backend (ocrCgAutoExtractController) : les deux doivent rester alignés.
 */
export const ROLE_SCOPED_BASE_TYPES: Partial<Record<CgDocType, CgDocType>> = {
  piece_identite_cotitulaire: 'piece_identite',
  piece_identite_cotitulaire_verso: 'piece_identite',
  piece_identite_hebergeur: 'piece_identite',
  piece_identite_hebergeur_verso: 'piece_identite',
  piece_identite_verso: 'piece_identite',
  permis_conduire_cotitulaire: 'permis_conduire',
  permis_conduire_cotitulaire_verso: 'permis_conduire',
  permis_conduire_verso: 'permis_conduire',
  // Miroir de ROLE_SCOPED_DOC_TYPES (backend) : deux Kbis possibles par dossier.
  kbis_vendeur: 'kbis',
  kbis_acheteur: 'kbis',
};

/**
 * Un appel OCR doit-il être retenté pour ce statut HTTP ?
 *
 * - `429` : rate limit Anthropic.
 * - `500` : le contrôleur auto-extract (`ocrCgAutoExtractController`) renvoie 500
 *   pour TOUTE erreur NON déterministe — hoquet/surcharge API (529 Overloaded),
 *   timeout, JSON modèle invalide. Les échecs DÉTERMINISTES (format non supporté,
 *   PDF illisible) sortent, eux, en `400` et ne sont donc PAS retentés. Un 500 sur
 *   cet endpoint vaut donc « réessaie » : sans ça, un simple pic de charge pendant
 *   une rafale fige définitivement la pièce en « EN COURS » (cas assurance
 *   FH-451-ZR / DC-40245 — 1 fichier sur 13 planté sur un 529 transitoire).
 * - `502/503/504` : passerelle / timeout amont.
 * - `undefined` : aucune réponse (réseau coupé, ECONNRESET, timeout client) — le
 *   cas le PLUS transitoire, désormais retenté lui aussi.
 *
 * Coût de fausse positive : un 500 réellement déterministe (clé API absente) est
 * retenté 3× en vain puis échoue — acceptable car marginal.
 */
export const isRetriableOcrStatus = (status: number | undefined): boolean =>
  status === undefined || status === 429 || status === 500 || status === 502 || status === 503 || status === 504;

/**
 * Raison DESTINÉE À L'AGENCE pour une pièce en échec, ou `undefined` quand
 * l'échec n'a pas d'explication actionnable (on retombe alors sur le libellé
 * générique « Le fichier n'a pas pu être analysé »).
 *
 * Deux cas méritent un message honnête, parce que dans les deux l'agence
 * s'acharnerait à redéposer le même fichier :
 * - `UploadRejectedError` : refus à l'upload (trop lourd, format non accepté) ;
 * - `storageUnavailable` : le backend n'a pas pu RETÉLÉCHARGER la pièce depuis
 *   Firebase (403 AccessDenied). Le fichier est bon et déjà stocké — incident
 *   2026-08-20, Google Cloud Storage bloquait l'IPv6 du serveur de prod une
 *   requête sur deux, et Biwiz a recompressé puis redéposé 3 dossiers pour rien.
 */
export const deriveUserFacingReason = (error: any): string | undefined => {
  if (error instanceof UploadRejectedError) return error.message;
  if (error?.response?.data?.storageUnavailable === true) {
    const reason = error.response.data.error;
    return typeof reason === 'string' && reason.length > 0 ? reason : undefined;
  }
  return undefined;
};

/**
 * Détail du pipeline backend renvoyé par /api/ocr/cg/auto-extract —
 * miroir du champ `pipeline` construit dans `ocrCgAutoExtractController.ts`.
 * Sert exclusivement à la trace IA console (modèles, confiances, verdicts).
 */
interface AutoExtractPipelineInfo {
  classifierModel?: string;
  classifierConfidence?: number;
  classifiedType?: string;
  finalDocType?: string;
  extractionSkipped?: boolean;
  postValidation?: { ran: boolean; verdict?: string };
}

/** Pièce récupérée dans la MÊME image (ex : carte grise apposée sur une cession). */
interface AutoExtractAdditionalDoc {
  docType: CgDocType;
  data: CgOcrResponse;
  model?: string;
  provider?: string;
  /**
   * URL propre à cette pièce quand ce n'est pas celle du fichier : la 2e
   * personne d'un fichier (`<url>#personne-2`, CNI ou permis d'un couple
   * scannés ensemble). Même fichier, mais pièce distincte pour le pool
   * d'identités, le routage par les noms et le dossier.
   */
  imageUrl?: string;
}

interface AutoExtractApiResponse {
  success: boolean;
  data?: CgOcrResponse;
  model?: string;
  provider?: string;
  usage?: { prompt_tokens: number; completion_tokens: number; total_tokens: number };
  pipeline?: AutoExtractPipelineInfo;
  /** Pièces récupérées dans la même image (ex : carte grise apposée). */
  additionalDocs?: AutoExtractAdditionalDoc[];
  /**
   * Pièces signalées par le modèle dans le fichier mais que le backend n'a PAS
   * réussi à ré-extraire : rien n'est persisté pour elles, la checklist doit
   * continuer à les réclamer (cf. GT-062-CG, permis perdu sur un socket hang up).
   */
  unrecoveredContainedDocs?: { docType: string; reason: 'network' | 'not_conclusive' }[];
  /**
   * PDF multi-documents découpé par le backend (skip-446) : pas de `data`, le
   * fichier est remplacé par ses parties, analysées chacune comme un dépôt.
   */
  split?: { parts: PdfSplitPart[] };
  /** Déroulé interne du backend (étapes réelles), retranscrit en console sous [BACKEND]. */
  trace?: string[];
  error?: string;
}

/** Retranscrit en console le déroulé interne RÉEL du backend pour ce fichier. */
const traceBackendSteps = (fileName: string, backendTrace: string[] | undefined): void => {
  if (!backendTrace || backendTrace.length === 0) return;
  traceIa(
    'BACKEND',
    `"${fileName}" — retranscription du déroulé interne du serveur (${backendTrace.length} étapes) :\n` +
      backendTrace.map((line) => `  ▸ ${line}`).join('\n'),
  );
};

const callAutoExtract = async (
  imageUrl: string,
  fileName: string,
  suggestedDocType?: CgDocType,
  noSplit = false,
): Promise<AutoExtractApiResponse> => {
  let lastError: unknown = null;
  for (let attempt = 0; attempt <= RETRY_DELAYS_MS.length; attempt++) {
    try {
      if (attempt > 0) {
        traceIa(
          'ANALYSE',
          `"${fileName}" — nouvelle tentative d'appel OCR (essai ${attempt + 1}/${RETRY_DELAYS_MS.length + 1}) après erreur transitoire.`,
        );
      }
      const response = await makeAuthenticatedApiCall<AutoExtractApiResponse>(
        'post',
        '/api/ocr/cg/auto-extract',
        // `suggestedDocType` court-circuite le classifieur côté backend
        // (autoExtractCgDocument) : docTypeMatchConfidence = 1, provider
        // « suggested ». Omis quand non fourni pour ne rien changer au
        // comportement historique.
        // `noSplit` : partie déjà issue d'une découpe, le backend ne la
        // redécoupe pas (anti-boucle).
        {
          imageUrl,
          // Ce front sait traiter une réponse `split` : le serveur ne découpe
          // que pour lui (un onglet d'avant la mise en production ne l'envoie pas).
          splitSupported: true,
          ...(suggestedDocType ? { suggestedDocType } : {}),
          ...(noSplit ? { noSplit: true } : {}),
        },
      );
      return response.data;
    } catch (error: any) {
      lastError = error;
      const status = error?.response?.status;
      const isRetriable = isRetriableOcrStatus(status);
      if (!isRetriable || attempt >= RETRY_DELAYS_MS.length) {
        throw error;
      }
      traceIa(
        'ANALYSE',
        `"${fileName}" — l'appel OCR a échoué avec un statut HTTP ${status ?? 'réseau (aucune réponse)'} (transitoire). Retry dans ${RETRY_DELAYS_MS[attempt] / 1000}s.`,
      );
      await sleep(RETRY_DELAYS_MS[attempt]);
    }
  }
  throw lastError;
};

class Semaphore {
  private available: number;
  private waiters: Array<() => void> = [];

  constructor(concurrency: number) {
    this.available = concurrency;
  }

  async acquire(): Promise<void> {
    if (this.available > 0) {
      this.available -= 1;
      return;
    }
    await new Promise<void>((resolve) => {
      this.waiters.push(resolve);
    });
  }

  release(): void {
    const next = this.waiters.shift();
    if (next) {
      next();
    } else {
      this.available += 1;
    }
  }
}

// ─────────────────────────────────────────────────────────────
//  Snapshots de session — maintenus EN DEHORS de React
//
//  Une Map module-level, clé = inFlightId. Chaque entrée porte l'état
//  réel d'une session OCR (items + uploadedDocs + extractedFields).
//  Survit à :
//    - resetV2() (le store est wipé, le snapshot non)
//    - "Lancer une autre demande" (la session active du store change,
//      mais l'OCR de la précédente continue d'écrire dans son snapshot)
//    - unmount/remount du hook (route change)
//  C'est CE snapshot qu'on PATCH dans cg_in_flight_uploads.payload
//  pour que "Basculer sur ce dossier" rehydrate l'état FRAIS et reste
//  ISO avec les compteurs du toast.
// ─────────────────────────────────────────────────────────────

interface SessionSnapshot {
  ingestionItems: Record<string, IngestionItem>;
  uploadedDocs: Partial<Record<CgDocType, UploadedDocResult>>;
  extractedFields: Record<string, ExtractedFieldEntry>;
  /** Pool des pièces d'identité brutes — source du routage (personne × type × face). */
  identityDocsPool: IdentityPoolEntry[];
  /** Pool des extraits Kbis (miroir de session, cf. store). */
  kbisDocsPool: KbisPoolEntry[];
}

const sessionSnapshots = new Map<string, SessionSnapshot>();

// ─────────────────────────────────────────────────────────────
//  Sessions annulées ("Recommencer à zéro" / reset)
//
//  Les processItem sont lancés en tâche de fond (void processItem). Sur un
//  reset, le store est wipé mais ces promesses CONTINUENT : leurs uploads et
//  OCR se terminent puis réécrivent dans le store GLOBAL (recordDocResult
//  n'est PAS gardé par session), repolluant la nouvelle session — l'agence
//  « ne peut plus recommencer un dossier vierge ».
//
//  On marque donc la session annulée ici. Toute promesse encore en vol vérifie
//  ce Set avant CHAQUE écriture (item, doc, progress) et avant de consommer un
//  slot OCR : une session annulée n'écrit plus rien et ne dépense plus de tokens.
// ─────────────────────────────────────────────────────────────
const abortedSessions = new Set<string>();

/**
 * Annule une session d'ingestion : ses fichiers en cours d'upload/analyse ne
 * réécriront plus dans le store et ne lanceront plus d'extraction. Appelé par
 * "Recommencer à zéro". Idempotent.
 */
export const abortIngestionSession = (sessionId: string): void => {
  abortedSessions.add(sessionId);
  deleteSessionSnapshot(sessionId);
};

/** Prédicat partagé par les gardes de processItem ET les tests. */
export const isIngestionSessionAborted = (sessionId: string | undefined): boolean =>
  sessionId !== undefined && abortedSessions.has(sessionId);

const getOrCreateSnapshot = (sessionId: string): SessionSnapshot => {
  let snap = sessionSnapshots.get(sessionId);
  if (!snap) {
    snap = { ingestionItems: {}, uploadedDocs: {}, extractedFields: {}, identityDocsPool: [], kbisDocsPool: [] };
    sessionSnapshots.set(sessionId, snap);
  }
  return snap;
};

export const deleteSessionSnapshot = (sessionId: string): void => {
  sessionSnapshots.delete(sessionId);
};

/**
 * Seed le snapshot in-memory d'une session depuis un payload backend.
 * Indispensable après "Basculer sur ce dossier" et après refresh navigateur :
 * sans ça, un nouveau dépôt de fichiers dans la session resume reset les
 * compteurs à zéro et écrase le payload backend.
 */
export const seedSessionSnapshot = (sessionId: string, data: Partial<SessionSnapshot>): void => {
  sessionSnapshots.set(sessionId, {
    ingestionItems: data.ingestionItems ?? {},
    uploadedDocs: data.uploadedDocs ?? {},
    extractedFields: data.extractedFields ?? {},
    identityDocsPool: data.identityDocsPool ?? [],
    kbisDocsPool: data.kbisDocsPool ?? [],
  });
};

interface BatchCounters {
  uploaded: number;
  analyzed: number;
  total: number;
}

export interface ProgressEvent {
  counters: BatchCounters;
  /** Snapshot complet de la session — à embarquer dans le payload backend. */
  snapshot: SessionSnapshot;
}

export type IngestionProgressCallback = (event: ProgressEvent) => void;

export interface StartIngestionOptions {
  /** Id de la cg_in_flight_uploads row à laquelle ces fichiers appartiennent. */
  sessionId?: string;
  /** Notifié à chaque transition d'item (uploadé, analysé, erreur). */
  onProgress?: IngestionProgressCallback;
  /**
   * Force le type de document au lieu de laisser le classifieur décider.
   *
   * Nécessaire pour les pièces déposées dans une zone d'upload DÉDIÉE dont le
   * rôle est connu de la zone elle-même mais indevinable à l'œil : la pièce
   * d'identité du co-titulaire est une CNI ordinaire, visuellement identique à
   * celle de l'acquéreur — le classifieur Haiku sortira toujours
   * `piece_identite`. Le type est donc IMPOSÉ par la provenance du dépôt, ce
   * qui est la seule information fiable dont on dispose.
   *
   * Même stratégie que `piece_identite_hebergeur`, à ceci près que l'hébergeur
   * est routé a posteriori (overflow multi-CNI) alors qu'ici on le sait dès le
   * dépôt.
   */
  suggestedDocType?: CgDocType;
}

type ProcessItem = (
  item: IngestionItem,
  file: File | null,
  counters: BatchCounters,
  sessionId: string | undefined,
  onProgress: IngestionProgressCallback | undefined,
  suggestedDocType: CgDocType | undefined,
  noSplit?: boolean,
) => Promise<void>;

export const useAutoExtractQueue = () => {
  const uploadSemaphoreRef = useRef(new Semaphore(MAX_CONCURRENT_UPLOADS));
  const ocrSemaphoreRef = useRef(new Semaphore(MAX_CONCURRENT_OCR));
  // Les parties d'un PDF découpé sont analysées par processItem lui-même.
  const processItemRef = useRef<ProcessItem | null>(null);

  const processItem = useCallback<ProcessItem>(
    async (
      item: IngestionItem,
      /** `null` = fichier déjà uploadé, l'URL est portée par `item.firebaseUrl`. */
      file: File | null,
      counters: BatchCounters,
      sessionId: string | undefined,
      onProgress: IngestionProgressCallback | undefined,
      suggestedDocType: CgDocType | undefined,
      noSplit = false,
    ) => {
      // Session annulée ("Recommencer") ? Une promesse encore en vol ne doit
      // plus rien écrire ni analyser. Vérifié à chaque écriture + avant l'OCR.
      const isAborted = () => isIngestionSessionAborted(sessionId);
      if (isAborted()) return;

      // Snapshot de session — toujours alimenté, indépendamment du store.
      const snap = sessionId ? getOrCreateSnapshot(sessionId) : null;
      if (snap) snap.ingestionItems[item.id] = { ...item };

      /**
       * Update du store + snapshot session.
       *
       * Le snapshot module-level (Map par sessionId) garde l'historique
       * complet hors React pour la persistance cg_in_flight_uploads.payload :
       * il est TOUJOURS écrit, même pour un dossier qui n'est plus à l'écran —
       * c'est ce qui permet de laisser les analyses vivre en arrière-plan.
       *
       * Le store, lui, alimente l'UI et ne représente que le dossier affiché :
       * il est protégé par `writesToDisplayedSession()` ci-dessous.
       *
       * Historique du gating, à ne pas refaire à l'envers :
       * - gating strict `currentActiveId !== sessionId` (mai 2026) → droppait
       *   pendant la fenêtre transitoire entre startUpload() et
       *   setActiveInFlightId() : items figés sur "EN COURS" alors que la trace
       *   confirmait l'extraction (DC-39628, juin 2026) ;
       * - suppression totale du gating (e55ed6e4) → correcte tant que
       *   `recordDocResult` ne posait qu'une case, devenue dangereuse quand
       *   d81af1d6 en a fait un recalcul global des slots d'identité ;
       * - gating fin (ci-dessous) : ne rejette que si les deux ids sont connus
       *   ET différents, ce qui laisse passer la fenêtre transitoire.
       */
      /**
       * Le store global ne reflète QUE le dossier affiché à l'écran. On écrit
       * dedans sauf si ce résultat appartient de façon certaine à un AUTRE
       * dossier — c'est-à-dire si les deux ids sont connus et diffèrent.
       *
       * Volontairement permissif quand `activeInFlightId` est null : c'est la
       * fenêtre transitoire entre `startUpload()` et `setActiveInFlightId()`,
       * dont l'ancien gating strict faisait un drop silencieux (DC-39628).
       */
      const writesToDisplayedSession = (): boolean => {
        if (sessionId === undefined) return true;
        const activeId = useFormCGIAStore.getState().activeInFlightId;
        if (activeId === null) return true;
        return activeId === sessionId;
      };

      const writeStoreItem = (id: string, patch: Partial<IngestionItem>) => {
        if (isAborted()) return;
        if (snap) {
          const existing = snap.ingestionItems[id];
          if (existing) snap.ingestionItems[id] = { ...existing, ...patch };
        }
        // `updateIngestionItem` est déjà no-op sur un id inconnu du store ; le
        // gating fin ajoute la protection du cas où deux dossiers coexistent.
        if (!writesToDisplayedSession()) return;
        useFormCGIAStore.getState().updateIngestionItem(id, patch);
      };

      const writeDocResult = (result: UploadedDocResult) => {
        if (isAborted()) return;
        // Intégration partagée store ↔ snapshot (cf. integrateDocResult) : les
        // pièces d'identité entrent dans un POOL puis TOUS les slots identité
        // sont re-routés (personne × type × face) par identityRouting — routage
        // déterministe piloté par le Cerfa 13750 / cession / mandat /
        // attestation, indépendant de l'ordre d'arrivée OCR. Remplace l'ancien
        // overflow multi-CNI (P3.3) qui égarait le verso du co-titulaire en
        // slot hébergeur (retour François FX-187-XA).
        //
        // ⚠ On n'intègre que dans le snapshot de la session (pas le store
        // global comme référence) : un résidu Zustand d'une session précédente
        // fausserait le routage de la nouvelle session.
        if (snap) {
          // trace: true UNIQUEMENT ici — l'intégration miroir du store (juste
          // après) tracerait en double dans le journal IA.
          const integrated = integrateDocResult(
            {
              uploadedDocs: snap.uploadedDocs,
              extractedFields: snap.extractedFields,
              identityDocsPool: snap.identityDocsPool,
              kbisDocsPool: snap.kbisDocsPool,
            },
            result,
            { trace: true },
          );
          snap.uploadedDocs = integrated.uploadedDocs;
          snap.extractedFields = integrated.extractedFields;
          snap.identityDocsPool = integrated.identityDocsPool;
          snap.kbisDocsPool = integrated.kbisDocsPool;
          snap.ingestionItems = retagIngestionItems(snap.ingestionItems, integrated.identitySlotByUrl);
        }
        // Gating FIN (et non plus l'égalité stricte de DC-39628, qui droppait
        // pendant la fenêtre transitoire) : on n'écarte l'écriture que si les
        // deux ids sont connus ET différents. `activeInFlightId` encore null =
        // session en cours de publication → on écrit, comme avant.
        //
        // Nécessaire depuis d81af1d6 : `recordDocResult` ne pose plus une case,
        // il RECALCULE tous les slots d'identité du dossier affiché à partir du
        // pool. Un résultat tardif du dossier B entrait donc dans le pool de A
        // et réattribuait les pièces de A. Le snapshot de session (ci-dessus) a
        // déjà reçu le résultat et le payload backend de B est patché par
        // `reportProgress` : rien n'est perdu, seul l'écran est protégé.
        if (!writesToDisplayedSession()) {
          console.warn(
            `[CGIA] Résultat OCR ${result.docType} ignoré pour l'affichage : il appartient au dossier ${sessionId}, ` +
              `l'écran affiche ${useFormCGIAStore.getState().activeInFlightId}. Le dossier d'origine reste à jour.`,
          );
          return;
        }
        useFormCGIAStore.getState().recordDocResult(result);
      };

      const reportProgress = () => {
        if (isAborted()) return;
        if (!onProgress) return;
        try {
          onProgress({
            counters: { ...counters },
            snapshot: snap
              ? {
                  ingestionItems: { ...snap.ingestionItems },
                  uploadedDocs: { ...snap.uploadedDocs },
                  extractedFields: { ...snap.extractedFields },
                  identityDocsPool: [...snap.identityDocsPool],
                  kbisDocsPool: [...snap.kbisDocsPool],
                }
              : { ingestionItems: {}, uploadedDocs: {}, extractedFields: {}, identityDocsPool: [], kbisDocsPool: [] },
          });
        } catch (error) {
          console.error('onProgress threw:', error);
        }
      };

      try {
        // ── Upload Firebase ──
        // `file === null` : le fichier est DÉJÀ sur Firebase (zone d'upload qui
        // gère elle-même son upload, cf. `ingestUploadedUrls`). On saute
        // l'upload et on part directement de l'URL portée par l'item — sans ça
        // le fichier serait stocké deux fois et l'URL enregistrée dans le
        // dossier ne serait pas celle analysée.
        let firebaseUrl: string;
        if (file === null) {
          if (!item.firebaseUrl) {
            writeStoreItem(item.id, { status: 'error', errorMessage: 'URL de fichier manquante' });
            counters.uploaded += 1;
            counters.analyzed += 1;
            reportProgress();
            return;
          }
          firebaseUrl = item.firebaseUrl;
          counters.uploaded += 1;
          reportProgress();
          traceIa(
            'UPLOAD',
            `"${item.fileName}" — déjà présent sur Firebase (upload géré par la zone de dépôt). URL transmise à l'OCR: ${firebaseUrl}`,
          );
        } else {
          await uploadSemaphoreRef.current.acquire();
          // Annulé pendant l'attente du slot d'upload → on libère et on sort.
          if (isAborted()) {
            uploadSemaphoreRef.current.release();
            return;
          }
          writeStoreItem(item.id, { status: 'uploading' });
          traceIa(
            'UPLOAD',
            `"${item.fileName}" (${Math.round(item.fileSize / 1024)} Ko, ${item.mimeType || 'type inconnu'}) — upload vers Firebase Storage démarré (outil: SDK firebase/storage, dossier "images/", max ${MAX_CONCURRENT_UPLOADS} uploads simultanés).`,
          );
          try {
            firebaseUrl = await uploadFileToFirebase(file, 'images');
          } finally {
            uploadSemaphoreRef.current.release();
          }
          writeStoreItem(item.id, { firebaseUrl });
          counters.uploaded += 1;
          reportProgress();
          traceIa('UPLOAD', `"${item.fileName}" — upload Firebase terminé. URL transmise à l'OCR: ${firebaseUrl}`);
        }

        // ── OCR auto-extract ──
        await ocrSemaphoreRef.current.acquire();
        // Annulé pendant l'attente du slot OCR → on libère SANS lancer l'appel
        // modèle (aucun token dépensé pour une session abandonnée).
        if (isAborted()) {
          ocrSemaphoreRef.current.release();
          return;
        }
        writeStoreItem(item.id, { status: 'analyzing' });
        traceIa(
          'ANALYSE',
          `"${item.fileName}" — envoi à POST /api/ocr/cg/auto-extract (max ${MAX_CONCURRENT_OCR} analyses simultanées). ` +
            `Pipeline backend en 2 temps via le SDK @anthropic-ai/sdk (client.messages.create) : ` +
            `1) CLASSIFICATION du type de document par un modèle léger Claude Haiku (~80 tokens de sortie), ` +
            `2) EXTRACTION typée des champs par le modèle OCR actif (Claude Sonnet par défaut, feature flag back-office) avec le prompt spécialisé du type détecté. ` +
            `Si le fichier est un PDF, il est d'abord rasterisé en PNG (échelle 2x, max 10 pages) avant l'appel au modèle.`,
        );
        try {
          const response = await callAutoExtract(firebaseUrl, item.fileName, suggestedDocType, noSplit);
          traceBackendSteps(item.fileName, response.trace);

          // ── PDF multi-documents découpé (skip-446) ──
          // Le fichier d'origine disparaît de la checklist au profit de ses
          // parties, analysées chacune comme un dépôt déjà uploadé. Toutes ses
          // pages sont dans les parties : rien n'est perdu, et le relecteur
          // ouvre directement le bon document au lieu du PDF complet.
          const splitParts = response.success ? (response.split?.parts ?? []) : [];
          if (splitParts.length > 0) {
            if (isAborted()) return;
            const partItems = buildSplitPartItems(item, splitParts);
            traceIa(
              'DECOUPE',
              `"${item.fileName}" — PDF de ${splitParts.length} documents découpé par le serveur : ` +
                splitParts.map((p, i) => `${partItems[i].fileName} = ${p.docType}`).join(', ') +
                '. Chaque partie est analysée séparément.',
            );
            if (snap) {
              delete snap.ingestionItems[item.id];
              partItems.forEach((partItem) => {
                snap.ingestionItems[partItem.id] = { ...partItem };
              });
            }
            if (writesToDisplayedSession()) {
              const store = useFormCGIAStore.getState();
              store.removeIngestionItem(item.id);
              store.enqueueIngestionItems(partItems);
            }
            // L'original sort des compteurs (il était compté uploadé), chaque
            // partie y entre et sera comptée uploadée puis analysée.
            counters.uploaded -= 1;
            counters.total += partItems.length - 1;
            reportProgress();
            partItems.forEach((partItem) => {
              void processItemRef.current?.(partItem, null, counters, sessionId, onProgress, undefined, true);
            });
            return;
          }
          if (!response.success || !response.data) {
            traceIa(
              'ANALYSE',
              `"${item.fileName}" — le backend a répondu en échec: ${response.error ?? 'Extraction échouée'}.`,
            );
            writeStoreItem(item.id, { status: 'error', errorMessage: response.error ?? 'Extraction échouée' });
            counters.analyzed += 1;
            reportProgress();
            return;
          }
          const data = response.data;
          const pipeline = response.pipeline;

          if (pipeline) {
            traceIa(
              'CLASSIFICATION',
              `"${item.fileName}" — type de document détecté: "${pipeline.classifiedType ?? data.docTypeDetected}" ` +
                `par le classifieur ${pipeline.classifierModel ?? 'inconnu'} ` +
                `(confiance classifieur: ${Math.round((pipeline.classifierConfidence ?? 0) * 100)}%).` +
                (pipeline.extractionSkipped
                  ? " Type non reconnu → l'extraction de champs a été court-circuitée (aucun appel au modèle d'extraction)."
                  : ''),
            );
          }

          if (data.unreadable) {
            traceIa(
              'ANALYSE',
              `"${item.fileName}" — le modèle a répondu unreadable=true : image trop dégradée pour extraire quoi que ce soit. Statut fichier → "unreadable".`,
            );
            writeStoreItem(item.id, {
              status: 'unreadable',
              docTypeDetected: isCgDocType(data.docTypeDetected) ? data.docTypeDetected : 'unknown',
              confidence: Math.round(data.docTypeMatchConfidence * 100),
            });
            counters.analyzed += 1;
            reportProgress();
            return;
          }

          const modelDocType = isCgDocType(data.docTypeDetected) ? data.docTypeDetected : 'unknown';
          // Miroir de la garde backend (`ROLE_SCOPED_DOC_TYPES` dans
          // ocrCgAutoExtractController) : un type imposé qui encode un RÔLE
          // (pièce du co-titulaire) prime sur la lecture du modèle, qui ne peut
          // que répondre le type de base — une CNI de co-titulaire est une CNI
          // ordinaire. L'override ne s'applique QUE si le modèle a confirmé le
          // type de base attendu : un document déposé par erreur dans la zone
          // co-titulaire garde son vrai type et reste visible comme tel.
          const suggestedBase = suggestedDocType ? ROLE_SCOPED_BASE_TYPES[suggestedDocType] : undefined;
          const detected =
            suggestedDocType && suggestedBase && modelDocType === suggestedBase ? suggestedDocType : modelDocType;
          const confidencePct = Math.round(data.docTypeMatchConfidence * 100);

          if (detected === 'unknown') {
            traceIa(
              'ANALYSE',
              `"${item.fileName}" — type de document non reconnu parmi les 11 types attendus (confiance ${confidencePct}%). Statut fichier → "mismatch", aucun champ retenu.`,
            );
            writeStoreItem(item.id, {
              status: 'mismatch',
              docTypeDetected: detected,
              confidence: confidencePct,
              errorMessage: 'Type de document non reconnu',
            });
            counters.analyzed += 1;
            reportProgress();
            return;
          }

          const usagePart = response.usage
            ? ` Tokens consommés: ${response.usage.prompt_tokens} entrée + ${response.usage.completion_tokens} sortie = ${response.usage.total_tokens}.`
            : '';
          traceIa(
            'EXTRACTION',
            `"${item.fileName}" — extraction des champs réalisée par le modèle ${response.model ?? 'inconnu'} (provider: ${response.provider ?? 'inconnu'}).${usagePart} ` +
              `Type confirmé: ${data.docTypeDetected} (docTypeMatch=${data.docTypeMatch}, confiance ${confidencePct}%). ` +
              `Warnings du modèle: ${data.warnings.length > 0 ? `[${data.warnings.join(', ')}]` : 'aucun'}.\n` +
              `Champs extraits (valeur / confiance / source / lecture brute / alternatives) :\n${formatOcrFields(data.fields)}`,
          );

          if (pipeline?.postValidation?.ran) {
            traceIa(
              'CONTRE-VERIFICATION',
              `"${item.fileName}" — l'immatriculation du certificat de cession avait une confiance < 85% (cause n°1 de rejet ANTS) : ` +
                `un second modèle (Claude Haiku) a relu l'image pour la valider. Verdict: "${pipeline.postValidation.verdict ?? 'inconnu'}" ` +
                `(confirmed → confiance remontée à 95% ; refuted → confiance plafonnée à 50% + warning "validation_immat_echouee").`,
            );
          }

          traceIa(
            'RESULTAT',
            `"${item.fileName}" — statut final: "ok". Pièce "${detected}" enregistrée dans le dossier, fusion des champs dans le formulaire (voir lignes [FUSION] qui suivent).`,
          );
          writeStoreItem(item.id, {
            status: 'ok',
            docTypeDetected: detected,
            confidence: confidencePct,
            docTypeMatch: data.docTypeMatch === true,
          });
          writeDocResult({
            docType: detected,
            firebaseUrls: [firebaseUrl],
            ocrResponse: data,
          });

          // Pièces APPOSÉES récupérées par le backend dans la même image (ex :
          // carte grise posée sur une cession), avec leurs VRAIS champs
          // ré-extraits. On les enregistre EN PREMIER : priorité aux champs
          // réels sur le fallback "contient_aussi_*" (champs vides) traité juste
          // après. Sans écraser un dépôt dédié déjà présent.
          const recoveredTypes = new Set<CgDocType>();
          (response.additionalDocs ?? []).forEach((extra) => {
            if (!isCgDocType(extra.docType) || !extra.data) return;
            recoveredTypes.add(extra.docType);
            // 2e personne du fichier : elle entre dans le pool d'identités à
            // côté de la première, jamais à sa place. Le routage par les noms
            // (13750, cession, attestation) lui donne son rôle.
            if (extra.imageUrl) {
              const nom = (extra.data.fields?.nom?.value as string | undefined) ?? '?';
              const prenom = (extra.data.fields?.prenom?.value as string | undefined) ?? '?';
              traceIa(
                'MULTI-DOC',
                `"${item.fileName}" — 2e personne récupérée dans le même fichier : "${extra.docType}" de ${prenom} ${nom}. ` +
                  `Ajoutée comme pièce distincte (${extra.imageUrl.slice(extra.imageUrl.lastIndexOf('#'))}), rôle attribué par les noms.`,
              );
              writeDocResult({ docType: extra.docType, firebaseUrls: [extra.imageUrl], ocrResponse: extra.data });
              return;
            }
            const alreadyReceived = snap
              ? Boolean(snap.uploadedDocs[extra.docType])
              : Boolean(useFormCGIAStore.getState().uploadedDocs[extra.docType]);
            if (alreadyReceived) {
              traceIa(
                'MULTI-DOC',
                `"${item.fileName}" — une pièce "${extra.docType}" a été récupérée dans cette image, mais elle a déjà un dépôt dédié : on ne l'écrase pas.`,
              );
              return;
            }
            const extraImmat = (extra.data.fields?.immatriculation?.value as string | undefined) ?? '?';
            traceIa(
              'MULTI-DOC',
              `"${item.fileName}" — pièce "${extra.docType}" RÉCUPÉRÉE dans la même image (apposée sur le document principal "${detected}", immat="${extraImmat}"). ` +
                `Enregistrée avec ses champs réels et fusionnée dans le formulaire — la checklist ne la réclamera plus.`,
            );
            writeDocResult({
              docType: extra.docType,
              firebaseUrls: [firebaseUrl],
              ocrResponse: extra.data,
            });
          });

          // Fichier multi-documents (ex : PDF contenant CNI + permis) : les
          // prompts signalent les pièces additionnelles via les warnings
          // "contient_aussi_<docType>", et le backend les RÉ-EXTRAIT (traitées
          // juste au-dessus). Celles qui restent ici sont celles que le backend
          // n'a PAS pu récupérer : rien n'est enregistré pour elles, ni en
          // cg_ocr_extractions ni dans les cross-checks.
          //
          // On les laissait autrefois passer pour « reçues » (mêmes URLs, champs
          // vides) afin de ne pas les réclamer deux fois. C'était un mensonge à
          // double détente, payé sur GT-062-CG le 21/08/2026 : la ré-extraction
          // du permis tombe sur un « socket hang up », l'écran affiche le permis
          // reçu, le serveur ne voit aucune pièce, une demande de permis est
          // ouverte à la soumission et le dossier reste collé en « pièces à
          // déposer ». Et le marquage lui-même ne survivait pas : un placeholder
          // d'identité n'entre pas dans `identityDocsPool`, donc la passe de
          // routage suivante l'évinçait (il partage son URL avec le document
          // porteur, qui est au pool).
          //
          // On réclame donc honnêtement la pièce. Mieux vaut un dépôt en trop
          // qu'un dossier bloqué sans que personne ne sache pourquoi.
          const unrecovered = new Map<CgDocType, string>(
            (response.unrecoveredContainedDocs ?? [])
              .filter((u) => isCgDocType(u.docType))
              .map((u) => [u.docType as CgDocType, u.reason]),
          );
          data.warnings.forEach((warning) => {
            const extraMatch = /^contient_aussi_(.+)$/.exec(warning);
            if (!extraMatch || !isCgDocType(extraMatch[1])) return;
            const extraDocType = extraMatch[1] as CgDocType;
            // Récupérée avec ses vrais champs juste au-dessus : rien à dire.
            if (recoveredTypes.has(extraDocType)) return;
            const alreadyReceived = snap
              ? Boolean(snap.uploadedDocs[extraDocType])
              : Boolean(useFormCGIAStore.getState().uploadedDocs[extraDocType]);
            if (alreadyReceived) {
              traceIa(
                'MULTI-DOC',
                `"${item.fileName}" — le modèle signale que ce fichier contient AUSSI une pièce "${extraDocType}" (warning "${warning}"), mais cette pièce a déjà un dépôt dédié : on ne l'écrase pas.`,
              );
              return;
            }
            const reason = unrecovered.get(extraDocType);
            traceIa(
              'MULTI-DOC',
              `"${item.fileName}" — le modèle signale une pièce "${extraDocType}" dans ce fichier (warning "${warning}"), ` +
                `mais elle n'a PAS pu être extraite` +
                `${reason === 'network' ? ' (échec réseau après plusieurs tentatives)' : reason === 'not_conclusive' ? " (ré-extraction non concluante : le modèle ne retrouve pas cette pièce dans le fichier)" : ''}. ` +
                `Aucune donnée n'est enregistrée pour elle : la checklist continue de la réclamer, à déposer dans son propre fichier.`,
            );
          });

          counters.analyzed += 1;
          reportProgress();
        } finally {
          ocrSemaphoreRef.current.release();
        }
      } catch (error: any) {
        const message =
          error?.response?.data?.error ?? error?.message ?? 'Erreur réseau pendant le traitement du document';
        // Refus d'upload explicable à l'agence (fichier trop lourd, format non
        // accepté) : on conserve la raison telle quelle pour l'afficher dans la
        // liste des pièces à redéposer, au lieu du générique « Le fichier n'a
        // pas pu être analysé » qui la laissait redéposer le même fichier.
        const userFacingReason = deriveUserFacingReason(error);
        // Même en erreur, le backend renvoie sa trace : on la retranscrit
        // pour comprendre jusqu'où le pipeline est allé avant d'échouer.
        traceBackendSteps(item.fileName, error?.response?.data?.trace);
        traceIa(
          'RESULTAT',
          `"${item.fileName}" — statut final: "error" (${message}). Aucune donnée de ce fichier n'est intégrée au dossier.`,
        );
        writeStoreItem(item.id, { status: 'error', errorMessage: message, userFacingReason });
        counters.analyzed += 1;
        reportProgress();
      }
    },
    [],
  );
  processItemRef.current = processItem;

  const startIngestion = useCallback(
    (items: IngestionItem[], filesById: Record<string, File>, options: StartIngestionOptions = {}) => {
      // Une session qui (re)démarre n'est plus annulée — défense si un id était
      // resté marqué (sécurité ; les ids de row sont normalement uniques).
      if (options.sessionId) abortedSessions.delete(options.sessionId);

      // Si on a déjà des items pour cette session, on REPREND les compteurs
      // depuis le snapshot persistant (ex : nouveau batch dans une session
      // déjà partiellement processée → la barre n'est pas remise à zéro).
      const existing = options.sessionId ? sessionSnapshots.get(options.sessionId) : null;
      const existingUploaded = existing
        ? Object.values(existing.ingestionItems).filter((it) => it.status !== 'queued' && it.status !== 'uploading')
            .length
        : 0;
      const existingAnalyzed = existing
        ? Object.values(existing.ingestionItems).filter(
            (it) => it.status !== 'queued' && it.status !== 'uploading' && it.status !== 'analyzing',
          ).length
        : 0;
      const existingTotal = existing ? Object.keys(existing.ingestionItems).length : 0;

      const counters: BatchCounters = {
        uploaded: existingUploaded,
        analyzed: existingAnalyzed,
        total: existingTotal + items.length,
      };

      traceIa(
        'BATCH',
        `Démarrage de l'ingestion de ${items.length} fichier(s)${options.sessionId ? ` (session ${options.sessionId})` : ''} : ` +
          `${items.map((it) => `"${it.fileName}"`).join(', ')}. ` +
          `Compteurs de session: ${counters.uploaded} uploadé(s) / ${counters.analyzed} analysé(s) / ${counters.total} au total. ` +
          `Chaque fichier suit le pipeline: upload Firebase → classification Claude Haiku → extraction typée → fusion des champs → cross-checks.`,
      );

      items.forEach((item) => {
        const file = filesById[item.id] ?? null;
        // Un item sans File est accepté s'il porte déjà son URL Firebase
        // (dépôt uploadé en amont — cf. ingestUploadedUrls). Sans URL ni
        // fichier, il n'y a rien à analyser.
        if (!file && !item.firebaseUrl) return;
        void processItem(item, file, counters, options.sessionId, options.onProgress, options.suggestedDocType);
      });
    },
    [processItem],
  );

  /**
   * Analyse des fichiers DÉJÀ uploadés sur Firebase, avec un type de document
   * imposé.
   *
   * Sert les zones de dépôt dédiées qui gèrent elles-mêmes leur upload et
   * conservent leur propre liste d'URLs — aujourd'hui la pièce d'identité du
   * co-titulaire (`SimpleUploadZone` → `coBuyerFiles`). Ces fichiers
   * continuent d'alimenter `coBuyerFilesUrls` exactement comme avant (donc
   * `CG_Documents.Type = 'coBuyerFiles'` et list-cg sont inchangés) ; on AJOUTE
   * simplement l'analyse OCR, qui manquait totalement.
   *
   * Retour François 2026-07-20 (EQ-598-TE) : « Pièce d'identité du cotitulaire
   * verso manquant et non remonté » — impossible à détecter tant que ces
   * fichiers ne produisaient aucune extraction.
   */
  const ingestUploadedUrls = useCallback(
    (urls: string[], docType: CgDocType, options: StartIngestionOptions = {}) => {
      if (urls.length === 0) return;
      const counters: BatchCounters = { uploaded: 0, analyzed: 0, total: urls.length };
      const items: IngestionItem[] = urls.map((url, index) => ({
        // Id déterministe dérivé de l'URL : re-déclencher l'ingestion du même
        // fichier réécrit le même item au lieu d'empiler un doublon dans la
        // checklist de l'agence.
        id: `preuploaded-${docType}-${url.slice(-32)}`,
        fileName: `Pièce co-titulaire ${index + 1}`,
        fileSize: 0,
        mimeType: '',
        status: 'queued' as const,
        firebaseUrl: url,
        createdAt: index,
      }));
      useFormCGIAStore.getState().enqueueIngestionItems(items);
      items.forEach((item) => {
        void processItem(item, null, counters, options.sessionId, options.onProgress, docType);
      });
    },
    [processItem],
  );

  return { startIngestion, ingestUploadedUrls, abortIngestionSession };
};
